import { createSelector, lruMemoize } from '@reduxjs/toolkit';

import type { AssetsAssetVersionSchema } from '@iconik-io/iconik-api/src/services/assets';

import { filterVersionsBySelectedVersionIds } from '../../../utils/filter_versions_by_selected_version_ids';
import { getPlaylistItemVersionIds } from '../../../utils/get_playlist_item_version_ids';
import type { BaseAppState } from '../../types';
import type { AssetsAssetVersionSchemaWithId } from '../normalized/asset_version';
import { getAssetOrSearchItem, selectAssetOrSearchItem } from './assets';

export type AssetsAssetVersionSchemaWithIdExtended =
  AssetsAssetVersionSchemaWithId & {
    versionNumber: number;
    promotedTo: string | undefined;
    name: string;
  };

export const getLatestActiveVersionId = lruMemoize(
  (versions: AssetsAssetVersionSchema[] | null | undefined) =>
    versions?.findLast((version) => version.status === 'ACTIVE')?.id,
);

export const getVersionsExtended = (
  versions: AssetsAssetVersionSchemaWithId[],
) => {
  const assetVersionIds = versions.map((version) => version.id);
  const versionsProcessed = versions.map((version, index) => {
    const isPromoted = assetVersionIds.lastIndexOf(version.id) > index;
    const result: AssetsAssetVersionSchemaWithIdExtended = {
      ...version,
      name: `v${index + 1}`,
      versionNumber: index + 1,
      promotedTo: isPromoted ? version.id : undefined,
    };
    return result;
  });

  return versionsProcessed;
};

export const selectAssetVersionsExtended = createSelector(
  getAssetOrSearchItem,
  (asset) => {
    if (!asset?.versions) {
      return [];
    }

    return getVersionsExtended(
      // biome-ignore lint/plugin: API boundary, the asset schema types versions without the id every returned version carries.
      asset.versions as AssetsAssetVersionSchemaWithId[],
    );
  },
);

export const selectAssetVersionsExtendedMap = createSelector(
  selectAssetVersionsExtended,
  (extendedVersions) => {
    return Object.fromEntries(
      extendedVersions.map((version) => [version.id, version]),
    );
  },
);

export const selectAssetVersionExtended = createSelector(
  selectAssetVersionsExtended,
  (
    _state: BaseAppState,
    _assetId: string | undefined,
    versionId: string | undefined,
  ) => versionId,
  (versions, versionId) => {
    if (!versionId) {
      return undefined;
    }
    return versions.findLast((version) => version.id === versionId);
  },
);

export const selectLatestActiveVersionExtended = createSelector(
  selectAssetVersionsExtended,
  (extendedVersions) => {
    return extendedVersions.findLast((version) => version.status === 'ACTIVE');
  },
);

export const selectLatestActiveVersionId = createSelector(
  getAssetOrSearchItem,
  (asset) => {
    const versions = asset?.versions;
    if (!versions) {
      return undefined;
    }
    return versions.findLast((version) => version.status === 'ACTIVE')?.id;
  },
);

export const selectIsOnlyActiveVersion = createSelector(
  selectAssetVersionsExtended,
  (
    _state: BaseAppState,
    _assetId: string | undefined,
    versionId: string | undefined,
  ) => versionId,
  (versions, versionId) => {
    const activeVersions = versions.filter(
      (version) => version.status === 'ACTIVE',
    );

    return (
      versions.length > 1 &&
      activeVersions.length === 1 &&
      activeVersions[0].id === versionId
    );
  },
);

const getVersionId = (
  _state: BaseAppState,
  _assetId: string,
  versionId: string | undefined,
) => versionId;

export const selectCurrentVersionId = createSelector(
  selectAssetOrSearchItem,
  getVersionId,
  (asset, versionId) => {
    const versionIdList = getPlaylistItemVersionIds(versionId);
    // Specifically for playlist items, version id value due to specific API implementation is returned as a string
    // that can have a value of `latest` or a string separated by comma that includes a list of all versions
    // user selected to share in said playlist, here we check for such list and filter versions by it
    if (versionIdList) {
      const versions = asset?.versions ?? [];
      const filteredValues = filterVersionsBySelectedVersionIds(
        versions,
        versionIdList,
      );
      const latestActiveVersion = filteredValues.findLast(
        (version) => version.status === 'ACTIVE',
      );

      return latestActiveVersion?.id;
    }

    if (versionId && versionId !== 'latest') {
      return versionId;
    }

    const latestActiveVersion = asset?.versions?.findLast(
      (version) => version.status === 'ACTIVE',
    );

    return latestActiveVersion?.id;
  },
);

export const selectIsLatestVersion = createSelector(
  selectAssetOrSearchItem,
  selectCurrentVersionId,
  (asset, currentVersionId) => {
    const latestVersionId = getLatestActiveVersionId(asset?.versions);
    return Boolean(currentVersionId && currentVersionId === latestVersionId);
  },
);
